import React from "react";
import styled from 'styled-components';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import DOMPurify from 'dompurify';
import {JSON_HEADERS, EMAIL_PATTERN } from "../constants/constants";
import he from 'he';

dayjs.extend(customParseFormat); // allows us to parse date by specifying the format used

const headerExceptions = {
    vendor_custom_tags: 'custom_tags',
    job_function: 'job_title'
}

function extractHeader(rawHeader, uppercaseHeaders = []) {
    let header = '';
    if (rawHeader in headerExceptions) {
        header = headerExceptions[rawHeader];
        header = header.replaceAll('_', " ");
    } else {
        header = rawHeader.replaceAll('_', " ");
    }

    const separateWord = header.toLowerCase().split(' ');
    for (var i = 0; i < separateWord.length; i++) {
        separateWord[i] = separateWord[i].charAt(0).toUpperCase() +
        separateWord[i].substring(1);

        if (uppercaseHeaders.includes(separateWord[i].toLowerCase())) {
            separateWord[i] = separateWord[i].toUpperCase();
        }
    }

    return separateWord.join(' ');
}

function renderResellerBadge() {
    return (
        <div className={'badge badge-soft-success'}>Reseller</div>
    )
}

function renderRatingStars(rating) {
    if (!rating || rating === '0') {
        return <StyledRatingDiv style={{display: 'flex', flexDirection: 'row'}}>
            No Rating
        </StyledRatingDiv>
    }
    let stars = [];
    for (let i = 0; i < Math.floor(rating); i++) {
        stars.push(<StyledI className="fas fa-star"></StyledI>);
    }
    if (rating % 1 > 0) {
        stars.push(<StyledI className="fas fa-star-half-alt"></StyledI>);
    }

    [...Array(5 - stars.length)].forEach(() => stars.push(<StyledI className="far fa-star"></StyledI>))

    return (
        <StyledRatingDiv style={{display: 'flex', flexDirection: 'row'}}>
            {
                stars.map((star, index) => {
                    return (
                        <div key={index}>
                            {star}
                        </div>
                    )
                })
            }
        </StyledRatingDiv>
    )
}

function subtractDate(amount, timeSpan) {
    return dayjs().subtract(amount, timeSpan);
}

function formatDate(dateString, dateFormat = 'MMM D, YYYY') {
    return dayjs(dateString, 'YYYY-MM-DD').format(dateFormat);
}

function sanitizePhoneNumber(phone) {
    return phone.replace(/[^+0-9]/g, '');
}

function formatDateForComparison(date) {
    return dayjs(date, 'YYYY-MM-DD').format('YYYY-MM-DD');
}

function isDateAfter(testedDate, date) {
    return dayjs(testedDate).isAfter(date);
}

function truncateString(str, allowedLength) {
    let trunStr = str.substring(0, allowedLength)
    if (str.length > allowedLength) {
        trunStr += '...';
    }
    return trunStr;
}
function searchContent(str, allowedLength, searchValue) {
    let trunStr = '';
    if (searchValue && str?.toLowerCase().includes(searchValue.toLowerCase())) {
        let pre = str.substring(0, str.toLowerCase().indexOf(searchValue.toLowerCase()));
        let val = str.substring(str.toLowerCase().indexOf(searchValue.toLowerCase()), str.toLowerCase().indexOf(searchValue.toLowerCase()) + searchValue.length);
        let suf = str.substring(str.toLowerCase().indexOf(searchValue.toLowerCase()) + searchValue.length, str.length);

        if (allowedLength <= searchValue.length) {
            return (
                <span style={{color: 'red', fontWeight: 'bold'}}>{pre.length && '...'}{val.substring(0, allowedLength)}{(allowedLength < str.length) && '...'}</span>
            )
        }

        let margin = Math.ceil(Math.floor(allowedLength/2) - Math.floor(searchValue.length/2));

        let truncatePre = false;
        let truncateSuf = false;
        if (pre.length > allowedLength) {
            if (pre.toLowerCase().includes(searchValue.toLowerCase())) {
                pre = pre.substring(pre.length - margin, pre.length);
            } else {
                pre = pre.substring(pre.length - margin, pre.length);
            }
            truncatePre = true;
        }

        if (suf.length > allowedLength) {
            if (truncatePre) {
                suf = suf.substring(0, margin);
            } else {
                suf = suf.substring(0, margin + (margin - pre.length));
            }            
            truncateSuf = true;
        }

        return (
            <span style={{wordBreak: 'break-all'}}>
                {truncatePre && '...'}{pre}
                <span className={'text-warning fw-bold'}>{val}</span>
                {suf}{truncateSuf && '...'}
            </span>
        )
    } else {
        trunStr = str?.substring(0, allowedLength)
        if (str?.length > allowedLength) {
            trunStr += '...';
        }

        return (
            <span>
                {trunStr}
            </span>
        )
    }
}

function stripHTMLFromString(str) {
    var div = document.createElement("div");
    div.innerHTML = str;
    var strippedStr = div.textContent || div.innerText || "";
    return strippedStr;
} 

function createElementFromHTML(htmlString) {
    return (
        <div dangerouslySetInnerHTML={{ __html: he.decode(DOMPurify.sanitize(htmlString)) }}/>
    )
}

function formatDateForFilename(now=new Date()) {
    const pad = (n) => n.toString().padStart(2, '0');

    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);      // Months are 0-indexed
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());

    return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`
}

function copyToClipboard(str, e) {
    const el = e.currentTarget;
    const original = el.innerHTML;

    // ensure the width of the element doesn't change when the text is updated
    const w = el.offsetWidth;
    el.style.width = w + 'px';

    navigator.clipboard.writeText(str).then(() => {
        el.innerHTML = "✓ Copied";
        setTimeout(() => {
            el.innerHTML = original;
            el.style.width = '';
        }, 1000);
    });
}

const StyledRatingDiv = styled.div`
    & > div:not(:first-child) {
        padding-left: 5px;
    }
`;

const StyledI = styled.i`
    color: #FFCC00;
`;

function valueExists(value) {
    return value !== null && value !== undefined && value !== '';
}

async function productSearch(val, searchUrl=null, productsOnly=true) {
    if (val?.length < 3) {
        return [];
    }

    let url = searchUrl || '/products/autocomplete_list';

    try {
        const search = await fetch(`${url}?select2=true&eol=false&has_reviews=true&term=${val.toLowerCase()}`, {
            method: 'GET', headers: JSON_HEADERS });

        if(productsOnly) {
            const { results } = await search.json();
            return results;
        } else {
            return await search.json();
        }

    } catch (error) {
        console.error(error);
        return [];
    }
}

async function userSearch(val, internalOnly=false, searchUrl=null) {
    if (val?.length < 3) {
        return [];
    }

    let url = searchUrl || '/users/autocomplete_list';

    try {
        const search = await fetch(`${url}?select2=true&itcs_only=${internalOnly}&term=${val.toLowerCase()}`, {
            method: 'GET', headers: JSON_HEADERS });

        const { results } = await search.json();

        return results;

    } catch (error) {
        console.error(error);
        return [];
    }
}

async function validateEmail(value, fieldName, isEmailValid, setIsEmailValid, setEmailError, setError, clearErrors, returnError=false) {
    let abort;
    let val = value?.trim();
    if(val?.length < 3) {
        clearErrors(fieldName);
        setIsEmailValid(false);
        setEmailError(null);
        return;
    } else if(!EMAIL_PATTERN.test(val) && returnError) {
        return 'A valid email address is required.';
    } else if(!isEmailValid && EMAIL_PATTERN.test(val)) {
        abort?.abort();
        abort = new AbortController();
        clearErrors(fieldName);
        const response = await fetch(`/api/validate_new_email_address?existing_email=Y&verify=Y&user[email]=${val}`, { signal: abort.signal });
        const data = await response.json();

        if(data.error) {
            setError(fieldName, { type: 'manual', message: data.error });
            setEmailError(data.error);
            setIsEmailValid(false);
            if(returnError) return data.error
        } else {
            setIsEmailValid(true);
            setEmailError(null);
            clearErrors(fieldName);
            if(returnError) return null;
        }
    }
}

export {
    extractHeader,
    renderRatingStars,
    renderResellerBadge,
    formatDate,
    isDateAfter,
    searchContent,
    truncateString,
    stripHTMLFromString,
    formatDateForComparison,
    formatDateForFilename,
    subtractDate, 
    createElementFromHTML,
    sanitizePhoneNumber,
    copyToClipboard,
    productSearch,
    userSearch,
    validateEmail,
    valueExists
}